import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AiOutlineCalendar } from 'react-icons/ai';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../api/api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ToastMessage from '../utils/mui/righttoptoast';
import { getPracticeId } from '../config/practice';

// These were previously only registered as a side effect of unrelated modules
// (timesheets, inventory) happening to be imported first. Without them
// dayjs(x, "h : mm A") returns Invalid Date and .isSameOrBefore is undefined,
// so the slot grid silently saved empty.
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const DEFAULT_DURATION = '15';

// Half-day presets fill the same startTime/endTime the backend reads, so
// "Morning" and a hand-entered window are the same thing to the API.
const BLOCK_MODES = {
    FULL: { label: 'Full Day', startTime: '', endTime: '' },
    MORNING: { label: 'Morning', startTime: '00:00', endTime: '12:00' },
    AFTERNOON: { label: 'Afternoon', startTime: '12:00', endTime: '23:59' },
    CUSTOM: { label: 'Custom', startTime: '09:00', endTime: '12:00' },
};

const emptyDay = () => ({ timings: [], duration: DEFAULT_DURATION });

/** "13:30" -> "1 : 30 PM" (the format availabletimings is stored in). */
const formatTime = (time) => {
    const parsed = dayjs(time, 'HH:mm');
    if (!parsed.isValid()) return '';
    return parsed.format('h : mm A');
};

/** Expand display ranges into the discrete HH:mm grid the API books against. */
const generateTimeSlots = (timings, duration) => {
    const slots = [];
    const step = Number(duration) || Number(DEFAULT_DURATION);

    timings.forEach(({ start, end }) => {
        let startTime = dayjs(start, 'h : mm A');
        const endTime = dayjs(end, 'h : mm A');
        if (!startTime.isValid() || !endTime.isValid()) return;

        while (startTime.isBefore(endTime)) {
            slots.push(startTime.format('HH:mm'));
            startTime = startTime.add(step, 'minute');
        }
    });

    return slots;
};

const modeOf = (block) => {
    if (block.allDay) return 'FULL';
    if (block.startTime === BLOCK_MODES.MORNING.startTime
        && block.endTime === BLOCK_MODES.MORNING.endTime) return 'MORNING';
    if (block.startTime === BLOCK_MODES.AFTERNOON.startTime
        && block.endTime === BLOCK_MODES.AFTERNOON.endTime) return 'AFTERNOON';
    return 'CUSTOM';
};

const describeBlock = (block) => {
    const mode = modeOf(block);
    if (mode === 'CUSTOM') {
        return `${formatTime(block.startTime)} - ${formatTime(block.endTime)}`;
    }
    return BLOCK_MODES[mode].label;
};

const ConfigModal = ({ handlecloseConfig, providerConfig }) => {
    const [isLoading, setLoading] = useState(false);
    const practiseid = getPracticeId();

    const [selectedDays, setSelectedDays] = useState([]);
    const [dayConfigs, setDayConfigs] = useState({});
    const [activeDay, setActiveDay] = useState('MON');
    const [newTimeSlot, setNewTimeSlot] = useState({ start: '', end: '' });

    const [blockedDates, setBlockedDates] = useState([]);
    const [newBlockedDate, setNewBlockedDate] = useState({ start: '', end: '' });
    const [blockMode, setBlockMode] = useState('FULL');
    const [blockWindow, setBlockWindow] = useState({
        startTime: BLOCK_MODES.CUSTOM.startTime,
        endTime: BLOCK_MODES.CUSTOM.endTime,
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

    const activeConfig = dayConfigs[activeDay] || emptyDay();
    const isActiveDayEnabled = selectedDays.includes(activeDay);

    const toggleDay = (day) => {
        setActiveDay(day);
        setSelectedDays((prev) => {
            if (prev.includes(day)) return prev.filter((d) => d !== day);
            // Enabling a fresh day: give it somewhere to put hours.
            setDayConfigs((configs) => (
                configs[day] ? configs : { ...configs, [day]: emptyDay() }
            ));
            return [...prev, day];
        });
    };

    const updateActiveDay = (patch) => {
        setDayConfigs((prev) => ({
            ...prev,
            [activeDay]: { ...(prev[activeDay] || emptyDay()), ...patch },
        }));
    };

    const handleAddTimeSlot = () => {
        if (!newTimeSlot.start || !newTimeSlot.end) {
            setToast({ open: true, message: 'Pick both a start and an end time', severity: 'info' });
            return;
        }
        if (newTimeSlot.start >= newTimeSlot.end) {
            setToast({ open: true, message: 'End time must be after start time', severity: 'info' });
            return;
        }

        updateActiveDay({
            timings: [
                ...activeConfig.timings,
                { start: formatTime(newTimeSlot.start), end: formatTime(newTimeSlot.end) },
            ],
        });
        setNewTimeSlot({ start: '', end: '' });
    };

    const removeTimeSlot = (index) => {
        updateActiveDay({ timings: activeConfig.timings.filter((_, i) => i !== index) });
    };

    /** Most providers keep one weekly pattern; this keeps that a single click. */
    const copyToAllDays = () => {
        setDayConfigs((prev) => {
            const next = { ...prev };
            selectedDays.forEach((day) => {
                next[day] = {
                    timings: activeConfig.timings.map((t) => ({ ...t })),
                    duration: activeConfig.duration,
                };
            });
            return next;
        });
        setToast({
            open: true,
            message: `Applied ${activeDay} hours to all selected days`,
            severity: 'success',
        });
    };

    const handleAddBlockedDate = () => {
        if (!newBlockedDate.start) return;

        const format = (d) => dayjs(d).format('M-D-YYYY');
        const preset = BLOCK_MODES[blockMode];
        const isFullDay = blockMode === 'FULL';
        const window = blockMode === 'CUSTOM' ? blockWindow : preset;

        if (!isFullDay && window.startTime >= window.endTime) {
            setToast({ open: true, message: 'Block end time must be after start time', severity: 'info' });
            return;
        }

        setBlockedDates((prev) => [
            ...prev,
            {
                start: format(newBlockedDate.start),
                // A single-day block comes back with no end date selected.
                end: format(newBlockedDate.end || newBlockedDate.start),
                allDay: isFullDay,
                startTime: isFullDay ? '' : window.startTime,
                endTime: isFullDay ? '' : window.endTime,
            },
        ]);
        setNewBlockedDate({ start: '', end: '' });
        setBlockMode('FULL');
    };

    const removeBlockedDate = (index) => {
        setBlockedDates((prev) => prev.filter((_, i) => i !== index));
    };

    const fetchConfigData = async () => {
        setLoading(true);
        try {
            const response = await api.get(
                `/v1/${practiseid}/provider/${providerConfig.providerid}/data`
            );

            // Read the object form, not result.config -- the same endpoint also
            // returns config JSON-string-encoded under result.
            const data = response?.provider?.config;
            if (response?.status !== 200 || !data) {
                setSelectedDays([]);
                setDayConfigs({});
                return;
            }

            const days = Array.isArray(data.availableDays) && data.availableDays.length
                ? data.availableDays.map((d) => String(d).toUpperCase())
                : (data.weekDays || []).map((d) => String(d).toUpperCase());

            const configs = {};

            if (Array.isArray(data.days) && data.days.length) {
                // Day-wise config: each day carries its own hours and duration.
                data.days.forEach((item) => {
                    const key = String(item?.day || '').toUpperCase();
                    if (!DAYS.includes(key)) return;
                    configs[key] = {
                        timings: (item.availabletimings || []).map((slot) => {
                            const [start, end] = String(slot).split(' - ');
                            return { start, end };
                        }),
                        duration: String(item.timeduration || DEFAULT_DURATION),
                    };
                });
            } else {
                // Flat config: one schedule shared across every available day.
                // Seeding each day with it means an existing provider opens
                // with today's hours intact and becomes day-wise on first save.
                const shared = {
                    timings: (data.availabletimings || []).map((slot) => {
                        const [start, end] = String(slot).split(' - ');
                        return { start, end };
                    }),
                    duration: String(data.timeduration || DEFAULT_DURATION),
                };
                days.forEach((day) => {
                    configs[day] = {
                        timings: shared.timings.map((t) => ({ ...t })),
                        duration: shared.duration,
                    };
                });
            }

            setSelectedDays(days);
            setDayConfigs(configs);
            if (days.length) setActiveDay(days[0]);

            setBlockedDates((data.blockedDates || []).map((entry) => {
                // Both shapes live in the wild: legacy "M-D-YYYY - M-D-YYYY"
                // strings and the object form with an optional time window.
                if (typeof entry === 'string') {
                    const [start, end] = entry.split(' - ');
                    return { start, end, allDay: true, startTime: '', endTime: '' };
                }
                return {
                    start: entry.start,
                    end: entry.end || entry.start,
                    allDay: entry.allDay !== false,
                    startTime: entry.startTime || '',
                    endTime: entry.endTime || '',
                };
            }).filter((b) => b.start));
        } catch (error) {
            console.error(error);
            setToast({ open: true, message: 'Could not load provider configuration', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerConfig]);

    const fetchUpdateConfigData = async () => {
        setLoading(true);
        try {
        // QA 2026-08-24: an Update click produced NO request and NO feedback
        // (zero configDayWise PUTs in the server log during the session).
        // The payload build used to sit OUTSIDE the try, so any client-side
        // error died silently — everything now surfaces as a toast.
        if (!providerConfig?.providerid) {
            setToast({ open: true, message: 'No provider id on this row — close and reopen the config', severity: 'error' });
            setLoading(false);
            return;
        }

        const payload = {
            config: {
                // The day-wise model types providerid as an int.
                providerid: Number(providerConfig.providerid),
                provider: providerConfig.username,
                days: selectedDays.map((day) => {
                    const cfg = dayConfigs[day] || emptyDay();
                    return {
                        day,
                        availabletimings: cfg.timings.map((t) => `${t.start} - ${t.end}`),
                        timeSlots: generateTimeSlots(cfg.timings, cfg.duration),
                        timeduration: String(cfg.duration),
                    };
                }),
                availableDays: selectedDays,
                weekDays: selectedDays,
                blockedDates,
                // blockeddays used to carry an expanded copy of every blocked
                // date. Nothing reads it as dates and the mirror drifted badly
                // (one provider had 4 real ranges against 182 entries), so
                // blockedDates is now the only source of truth.
                blockeddays: [],
            },
        };

            const response = await api.put(
                `/v1/${practiseid}/provider/configDayWise`,
                payload
            );

            if (response.status === 200) {
                setToast({ open: true, message: 'Data updated successfully', severity: 'success' });
            } else {
                setToast({
                    open: true,
                    message: response?.message || 'Something Went wrong',
                    severity: 'info',
                });
            }
        } catch (error) {
            console.error('[ConfigModal] update failed:', error);
            setToast({
                open: true,
                message: `Update failed: ${error?.response?.data?.message || error?.message || 'unknown error'}`,
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (<>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="relative flex flex-col w-[50%] max-h-[92vh] overflow-y-auto bg-white pb-2 p-3 rounded-lg shadow-lg text-left">
                <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="font-semibold text-lg">Update Provider Configuration</h3>
                    <IconButton onClick={handlecloseConfig} className="text-gray-600 hover:text-gray-700">
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </div>

                <div className="p-2 space-y-4">
                    <div>
                        <div className='font-medium'>Days of the Week</div>
                        <div className="text-xs text-gray-500 mb-2">
                            Select the days this provider works, then set hours for each day.
                        </div>
                        <div className="flex space-x-2">
                            {DAYS.map((day) => {
                                const enabled = selectedDays.includes(day);
                                const active = day === activeDay;
                                return (
                                    <button
                                        key={day}
                                        onClick={() => toggleDay(day)}
                                        className={`py-1 px-3 rounded ${enabled ? 'bg-black text-white' : 'bg-gray-200'} ${active ? 'ring-2 ring-offset-1 ring-black' : ''}`}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="border-t pt-3">
                        <div className="flex items-center justify-between">
                            <div className='font-semibold'>
                                Available Time Slots
                                <span className="ml-2 text-sm font-normal text-gray-500">{activeDay}</span>
                            </div>
                            {isActiveDayEnabled && selectedDays.length > 1 && (
                                <button
                                    onClick={copyToAllDays}
                                    className="text-sm py-1 px-2 border rounded text-gray-700"
                                >
                                    Copy to all selected days
                                </button>
                            )}
                        </div>

                        {!isActiveDayEnabled ? (
                            <div className="text-sm text-gray-500 mt-2">
                                {activeDay} is switched off. Select it above to give it hours.
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between gap-4 mt-2">
                                    <div className="flex flex-col w-1/2 font-medium">
                                        <label>Start Time</label>
                                        <input
                                            type="time"
                                            value={newTimeSlot.start}
                                            onChange={(e) => setNewTimeSlot({ ...newTimeSlot, start: e.target.value })}
                                            className="p-1 border rounded w-full"
                                        />
                                    </div>
                                    <div className="flex flex-col w-1/2 font-medium">
                                        <label>End Time</label>
                                        <input
                                            type="time"
                                            value={newTimeSlot.end}
                                            onChange={(e) => setNewTimeSlot({ ...newTimeSlot, end: e.target.value })}
                                            className="p-1 border rounded w-full"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleAddTimeSlot}
                                    className="mt-2 py-1 px-3 border-2 font-medium border-gray-200 rounded"
                                >
                                    +   Add Time Slot
                                </button>

                                <div className="flex flex-wrap gap-2 mt-2">
                                    {activeConfig.timings.map((slot, index) => (
                                        <div key={index} className="flex items-center border-[1.5px] font-medium border-gray-200 p-1 rounded">
                                            <AccessTimeIcon style={{ width: '14px' }} />
                                            <span className='ml-1'>{slot.start} - {slot.end}</span>
                                            <div
                                                onClick={() => removeTimeSlot(index)}
                                                className="text-red-500 cursor-pointer flex items-center justify-center ml-1 w-6 rounded-full bg-orange-100"
                                            >
                                                <CloseIcon style={{ width: '17px' }} />
                                            </div>
                                        </div>
                                    ))}
                                    {!activeConfig.timings.length && (
                                        <span className="text-sm text-gray-500">No hours set for {activeDay}.</span>
                                    )}
                                </div>

                                <div className="mt-3 font-medium">
                                    Duration per Patient (minutes) — {activeDay}
                                </div>
                                <div className="w-[30%]">
                                    <input
                                        type="number"
                                        min="1"
                                        value={activeConfig.duration}
                                        onChange={(e) => updateActiveDay({ duration: e.target.value })}
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="border-t pt-3">
                        <div className='font-medium'>Blocked Dates</div>
                        <div className="text-xs text-gray-500 mb-2">
                            Block a full day, a half day, or a custom window.
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {Object.entries(BLOCK_MODES).map(([key, mode]) => (
                                <button
                                    key={key}
                                    onClick={() => setBlockMode(key)}
                                    className={`py-1 px-3 rounded text-sm ${blockMode === key ? 'bg-black text-white' : 'bg-gray-200'}`}
                                >
                                    {mode.label}
                                </button>
                            ))}
                        </div>

                        {blockMode === 'CUSTOM' && (
                            <div className="flex gap-4 mt-2">
                                <div className="flex flex-col font-medium">
                                    <label className="text-sm">From</label>
                                    <input
                                        type="time"
                                        value={blockWindow.startTime}
                                        onChange={(e) => setBlockWindow({ ...blockWindow, startTime: e.target.value })}
                                        className="p-1 border rounded"
                                    />
                                </div>
                                <div className="flex flex-col font-medium">
                                    <label className="text-sm">To</label>
                                    <input
                                        type="time"
                                        value={blockWindow.endTime}
                                        onChange={(e) => setBlockWindow({ ...blockWindow, endTime: e.target.value })}
                                        className="p-1 border rounded"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex relative space-x-2 mt-2">
                            <div
                                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                className="flex items-center border-[1.5px] border-gray-200 p-1 rounded cursor-pointer"
                            >
                                <AiOutlineCalendar style={{ width: '14px' }} />
                                <span className="ml-1">
                                    {newBlockedDate.start
                                        ? `${dayjs(newBlockedDate.start).format('MM/DD/YYYY')} - ${dayjs(newBlockedDate.end || newBlockedDate.start).format('MM/DD/YYYY')}`
                                        : 'Select Date'}
                                </span>
                            </div>

                            <div className='absolute font-medium bottom-9 z-10'>
                                {isDatePickerOpen && (
                                    <DatePicker
                                        selected={newBlockedDate.start}
                                        onChange={(dates) => {
                                            const [start, end] = dates;
                                            setNewBlockedDate({ start, end });
                                        }}
                                        startDate={newBlockedDate.start}
                                        endDate={newBlockedDate.end}
                                        selectsRange
                                        inline
                                        dateFormat="MM/dd/yyyy"
                                    />
                                )}
                            </div>

                            <button
                                onClick={() => {
                                    setIsDatePickerOpen(false);
                                    handleAddBlockedDate();
                                }}
                                className="py-1 px-3 border-2 font-medium rounded"
                            >
                                +   Add
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-2">
                            {blockedDates.map((block, index) => (
                                <div key={index} className="flex items-center border-[1.5px] font-medium border-gray-200 p-1 rounded">
                                    <CalendarTodayIcon style={{ width: '14px' }} />
                                    <span className="ml-1">
                                        {dayjs(block.start, 'M-D-YYYY').format('MM/DD/YYYY')} - {dayjs(block.end, 'M-D-YYYY').format('MM/DD/YYYY')}
                                        <span className="text-gray-500"> · {describeBlock(block)}</span>
                                    </span>
                                    <div
                                        onClick={() => removeBlockedDate(index)}
                                        className="text-red-500 cursor-pointer flex items-center justify-center ml-1 w-6 rounded-full bg-orange-100"
                                    >
                                        <CloseIcon style={{ width: '17px' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <button
                        type="button"
                        onClick={fetchUpdateConfigData}
                        disabled={isLoading}
                        className="py-1 px-4 bg-black text-white rounded-lg disabled:opacity-50"
                    >
                        {isLoading ? 'Saving...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>

        <ToastMessage
            message={toast.message}
            severity={toast.severity}
            open={toast.open}
            handleClose={handleCloseToast}
        />
    </>);
};

export default ConfigModal;
